
import React, { useRef, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';

interface HanjaCanvasProps {
  hanja: {
    id: number;
    hanja: string;
    meaning: string;
    sound: string;
    strokes: string[];
  };
  currentIndex?: number; // Optional, for practice page
  totalHanja?: number; // Optional, for practice page
  penThickness: number;
  hanjaDisplaySize: number;
}

const HanjaCanvas: React.FC<HanjaCanvasProps> = ({ hanja, currentIndex, totalHanja, penThickness, hanjaDisplaySize }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const [isDrawing, setIsDrawing] = useState(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawBackgroundHanja();
      }
    }
  };

  const drawBackgroundHanja = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.font = `${hanjaDisplaySize * 0.8}px Arial`; // Adjust font size relative to canvas size
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = '#000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(hanja.hanja, canvas.width / 2, canvas.height / 2);
        ctx.globalAlpha = 1.0;
      }
    }
  };

  useEffect(() => {
    clearCanvas();
    
  }, [hanja, hanjaDisplaySize]);

  const startDrawing = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineWidth = penThickness;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
        ctx.beginPath();
        ctx.moveTo(event.nativeEvent.offsetX, event.nativeEvent.offsetY);
        setIsDrawing(true);
      }
    }
  };

  const draw = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineTo(event.nativeEvent.offsetX, event.nativeEvent.offsetY);
        ctx.stroke();
      }
    }
  };

  const startDrawingTouch = (event: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const touch = event.touches[0];
        const rect = canvas.getBoundingClientRect();
        const offsetX = touch.clientX - rect.left;
        const offsetY = touch.clientY - rect.top;

        ctx.lineWidth = penThickness;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
        ctx.beginPath();
        ctx.moveTo(offsetX, offsetY);
        setIsDrawing(true);
      }
    }
  };

  const drawTouch = (event: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const touch = event.touches[0];
        const rect = canvas.getBoundingClientRect();
        const offsetX = touch.clientX - rect.left;
        const offsetY = touch.clientY - rect.top;

        ctx.lineTo(offsetX, offsetY);
        ctx.stroke();
      }
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  return (
    <div>
      <h3>{hanja.hanja} ({hanja.meaning} - {hanja.sound}) {currentIndex !== undefined && totalHanja !== undefined && `(${currentIndex + 1}/${totalHanja})`}</h3>
      <canvas 
        ref={canvasRef} 
        width={hanjaDisplaySize} 
        height={hanjaDisplaySize} 
        style={{ border: '1px solid #000', touchAction: 'none' }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawingTouch}
        onTouchMove={drawTouch}
        onTouchEnd={stopDrawing}
      />
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Button variant="contained" onClick={clearCanvas} size="large">
          지우기
        </Button>
      </Box>
    </div>
  );
};

export default HanjaCanvas;
